import type { ReactNode } from 'react';
import { Alert, Button, Empty, Skeleton } from 'antd';
import { ArrowClockwise, WarningCircle } from '@phosphor-icons/react';
import { ApiError, NetworkError } from '../api/client';

/**
 * Bốn trạng thái mà 09-design-system bắt mọi màn phải có: đang tải, rỗng, lỗi nghiệp vụ, bị chặn.
 * Dùng chung để không màn nào "quên" một trạng thái rồi chỉ ship happy path.
 */

export function LoadingState({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-live="polite" aria-label="Đang tải dữ liệu">
      <Skeleton active paragraph={{ rows }} />
    </div>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return <Empty description={message}>{action}</Empty>;
}

/**
 * Lỗi nghiệp vụ hiển thị thẳng `message` từ server (đã là tiếng Việt, 05-api-conventions).
 * Lỗi mạng thì KHÔNG hiện message server mà mời thử lại — hai thứ này người dùng xử lý khác nhau.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const isNetwork = error instanceof NetworkError;
  const message =
    error instanceof ApiError || isNetwork
      ? error.message
      : 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.';

  return (
    <Alert
      type="error"
      showIcon
      // Không dùng riêng màu để truyền đạt trạng thái — luôn kèm icon và chữ (09-design-system)
      icon={<WarningCircle size={20} weight="fill" aria-hidden />}
      message={isNetwork ? 'Mất kết nối' : 'Không thực hiện được'}
      description={
        <>
          <div>{message}</div>
          {error instanceof ApiError && (
            <div style={{ marginTop: 4, fontSize: 11, opacity: 0.7 }}>Mã lỗi: {error.code}</div>
          )}
        </>
      }
      action={
        onRetry && (
          <Button size="small" onClick={onRetry} icon={<ArrowClockwise size={14} aria-hidden />}>
            Thử lại
          </Button>
        )
      }
    />
  );
}

/** Trạng thái bị chặn: đủ đăng nhập nhưng không đủ quyền cho thao tác (BR-801). */
export function BlockedState({ message }: { message: string }) {
  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningCircle size={20} weight="fill" aria-hidden />}
      message="Không đủ quyền"
      description={message}
    />
  );
}
